import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Mail, RefreshCw, Trash2, Inbox } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const CATEGORY_LABELS = {
  welcome:           { label: 'Bienvenue',          color: '#5BAFAF' },
  otp_reset:         { label: 'OTP Reset',          color: '#D4A14A' },
  password_changed:  { label: 'Mot de passe',       color: '#94a3b8' },
  mission_proposed:  { label: 'Mission proposée',   color: '#0E1B33' },
  mission_accepted:  { label: 'Mission acceptée',   color: '#5BAFAF' },
  mission_delivered: { label: 'Livraison',          color: '#D4A14A' },
  mission_completed: { label: 'Mission terminée',   color: '#5BAFAF' },
  kyc_validated:     { label: 'KYC validé',         color: '#5BAFAF' },
  kyc_rejected:      { label: 'KYC rejeté',         color: '#DC2626' },
  transactional:     { label: 'Autre',              color: '#94a3b8' },
};

function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AdminEmails() {
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterCat, setFilterCat] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearConfirm, setClearConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filterCat) params.category = filterCat;
      const r = await axios.get(`${API}/admin/dev-emails`, { params });
      setEmails(r.data.data || []);
      setTotal(r.data.meta?.total || 0);
    } finally { setLoading(false); }
  }, [filterCat]);

  useEffect(() => { load(); }, [load]);

  const openEmail = async (e) => {
    const r = await axios.get(`${API}/admin/dev-emails/${e.id}`);
    setSelected(r.data);
    setEmails(prev => prev.map(x => x.id === e.id ? { ...x, is_read: true } : x));
  };

  const doClearAll = async () => {
    setClearConfirm(false);
    await axios.delete(`${API}/admin/dev-emails`);
    setSelected(null);
    load();
  };

  const categories = Array.from(new Set(emails.map(e => e.category)));

  return (
    <div data-testid="admin-emails-page">
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontFamily: 'var(--f-display)', fontSize: 22, fontWeight: 800, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Inbox size={22} /> Boîte de dev — Emails transactionnels
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink2)' }}>
            {total} email{total > 1 ? 's' : ''} enregistré{total > 1 ? 's' : ''} · Mode <strong style={{ color: 'var(--gold)' }}>dev</strong> (aucun envoi réel)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load} data-testid="btn-emails-refresh">
            <RefreshCw size={14} /> Rafraîchir
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setClearConfirm(true)} data-testid="btn-emails-clear" style={{ color: 'var(--red)' }}>
            <Trash2 size={14} /> Vider
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button
          className={`btn btn-ghost btn-sm ${filterCat === '' ? 'active' : ''}`}
          style={{ background: filterCat === '' ? 'var(--ink)' : '', color: filterCat === '' ? '#fff' : '' }}
          onClick={() => setFilterCat('')}
          data-testid="filter-cat-all"
        >Tous</button>
        {Object.entries(CATEGORY_LABELS).filter(([k]) => categories.includes(k) || filterCat === k).map(([k, v]) => (
          <button key={k}
            className={`btn btn-ghost btn-sm ${filterCat === k ? 'active' : ''}`}
            style={{ background: filterCat === k ? v.color : '', color: filterCat === k ? '#fff' : v.color, borderColor: v.color }}
            onClick={() => setFilterCat(k)}
            data-testid={`filter-cat-${k}`}
          >{v.label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px, 1fr) 2fr', gap: 16, alignItems: 'flex-start' }}>
        <div className="card" style={{ padding: 0, maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }} className="skeleton" />
          ) : emails.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink3)' }}>
              <Mail size={36} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div>Aucun email pour l'instant</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Effectue un reset password / inscription pour en générer.</div>
            </div>
          ) : (
            emails.map(e => (
              <button
                key={e.id}
                onClick={() => openEmail(e)}
                data-testid={`email-row-${e.id}`}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '12px 14px', borderBottom: '1px solid var(--bdl)',
                  background: selected?.id === e.id ? 'var(--cream)' : (e.is_read ? '#fff' : 'rgba(212,161,74,0.06)'),
                  cursor: 'pointer', border: 'none', borderLeft: selected?.id === e.id ? '3px solid var(--gold)' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: (CATEGORY_LABELS[e.category] || {}).color || 'var(--ink3)' }}>
                    {(CATEGORY_LABELS[e.category] || { label: e.category }).label}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>{fmtDate(e.created_at)}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: e.is_read ? 500 : 700, color: 'var(--ink)', marginBottom: 2 }}>
                  {e.subject}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink3)' }}>→ {e.to}</div>
              </button>
            ))
          )}
        </div>

        <div className="card" style={{ minHeight: 400, overflow: 'hidden', padding: 0 }}>
          {!selected ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)' }}>
              <Mail size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Sélectionne un email à gauche pour afficher son contenu</div>
            </div>
          ) : (
            <div>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bdl)', background: 'var(--cream)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: (CATEGORY_LABELS[selected.category] || {}).color }}>
                    {(CATEGORY_LABELS[selected.category] || { label: selected.category }).label}
                  </span>
                  <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 50, background: selected.status === 'SENT' ? 'var(--tbg)' : 'var(--gpale)', color: selected.status === 'SENT' ? 'var(--teal)' : 'var(--gold)', fontWeight: 700 }}>
                    {selected.status}
                  </span>
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6, color: 'var(--ink)' }}>{selected.subject}</div>
                <div style={{ fontSize: 12, color: 'var(--ink2)' }}>
                  <strong>De :</strong> {selected.from}<br />
                  <strong>À :</strong> {selected.to}<br />
                  <strong>Envoyé :</strong> {fmtDate(selected.created_at)}
                </div>
              </div>
              <iframe
                title="email-preview"
                srcDoc={selected.html}
                style={{ width: '100%', height: 600, border: 'none', background: '#fff' }}
                data-testid="email-preview-iframe"
                sandbox=""
              />
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={clearConfirm}
        onClose={() => setClearConfirm(false)}
        onConfirm={doClearAll}
        variant="destructive"
        icon="trash"
        title="Vider la boîte de dev ?"
        subtitle="Tous les emails enregistrés seront supprimés définitivement."
        confirmLabel="Tout supprimer"
      />
    </div>
  );
}
